import React from "react";
import PropTypes from "prop-types";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Box, Typography, Chip } from "@mui/material";
import { DateTime } from "luxon";

const COLOR_PALETTE = [
  "#1976d2",
  "#dc004e",
  "#388e3c",
  "#ff9800",
  "#9c27b0",
  "#03a9f4",
  "#f44336",
  "#009688",
];

const CustomLegend = ({ seriesKeys, visibleSeries, toggleSeries }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      mb: 2,
      gap: 1,
    }}
  >
    {seriesKeys.map((key, index) => (
      <Chip
        key={key}
        label={key}
        onClick={() => toggleSeries(key)}
        sx={{
          backgroundColor: visibleSeries[key] ? "#e3f2fd" : "#f0f0f0",
          opacity: visibleSeries[key] ? 1 : 0.5,
          cursor: "pointer",
        }}
        avatar={
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: COLOR_PALETTE[index % COLOR_PALETTE.length],
              borderRadius: "50%",
              ml: 1,
            }}
          />
        }
      />
    ))}
  </Box>
);

const ValveStateCard = ({ state }) => (
  <Typography
    variant="subtitle2"
    sx={{
      ml: 2,
      px: 1.5,
      py: 0.5,
      borderRadius: 1,
      fontWeight: "medium",
      color: state === 0 ? "#616161" : "#2e7d32",
      backgroundColor: state === 0 ? "#eeeeee" : "#c8e6c9",
      border: "1px solid #ccc",
      fontSize: "0.875rem",
      display: "inline-block",
    }}
  >
    Valve State: {state === 0 ? "Closed" : "Open"}
  </Typography>
);

const ValveCountCard = ({ openCount, closeCount }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      ml: 2,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        px: 1.2,
        py: 0.5,
        // borderRadius: 1,
        // backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        fontWeight: 500,
        fontSize: "0.75rem",
        // border: "1px solid #c8e6c9",
      }}
    >
      Opened: <strong>{openCount}</strong>
    </Typography>
    <Typography
      variant="caption"
      sx={{
        px: 1.2,
        py: 0.5,
        // borderRadius: 1,
        // backgroundColor: "#ffebee",
        color: "#c62828",
        fontWeight: 500,
        fontSize: "0.75rem",
        // border: "1px solid #ffcdd2",
      }}
    >
      Closed: <strong>{closeCount}</strong>
    </Typography>
  </Box>
);

const ChartContainer = ({ title, children }) => (
  <Box
    sx={{
      border: "1px solid #e0e0e0",
      borderRadius: 2,
      height: 600,
      p: 3,
      mb: 3,
      backgroundColor: "#fff",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        textAlign: "center",
        mb: 2,
        fontWeight: "bold",
        color: "#22C55E",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const ChartBlock = ({
  title,
  data,
  keys,
  visibleSeries,
  toggleSeries,
  extra,
  connectionStatus,
  useBarChart,
  isFilterActive,
}) => {
  return (
    <ChartContainer
      title={
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {title}
          {extra}
        </Box>
      }
    >
      <CustomLegend
        seriesKeys={keys}
        visibleSeries={visibleSeries}
        toggleSeries={toggleSeries}
      />
      <Box sx={{ flexGrow: 1, width: "100%", height: "100%" }}>
        {connectionStatus === "Disconnected" ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "error.main",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
              ❌
            </Typography>
            <Typography variant="body1">Cannot connect to WebSocket</Typography>
          </Box>
        ) : data.length === 0 && isFilterActive ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "text.secondary",
              textAlign: "center",
              px: 4,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "medium" }}>
              No data matched the selected filter options
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {useBarChart ? (
              <BarChart
                data={data
                  .map((d) => ({
                    ...d,
                    __millis: DateTime.fromFormat(
                      d.timestamp,
                      "M/d/yyyy, h:mm:ss a"
                    ).toMillis(),
                  }))
                  .sort((a, b) => a.__millis - b.__millis)}
                margin={{ top: 20, right: 30, left: 30, bottom: 90 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={(val) =>
                    DateTime.fromFormat(val, "M/d/yyyy, h:mm:ss a").toFormat(
                      "HH:mm:ss"
                    )
                  }
                  interval="preserveStartEnd"
                  tick={{ fontSize: 13, angle: -30, textAnchor: "end" }}
                  height={80}
                  label={{
                    value: "Time",
                    position: "insideBottom",
                    offset: 0,
                    fontSize: 13,
                    fill: "#333",
                  }}
                />
                <YAxis
                  label={{
                    value: "Value",
                    angle: -90,
                    position: "insideLeft",
                    offset: -10,
                    fontSize: 14,
                    fill: "#333",
                  }}
                />
                <Tooltip />
                {keys.map((key, index) =>
                  visibleSeries[key] ? (
                    <Bar
                      key={key}
                      dataKey={key}
                      fill={COLOR_PALETTE[index % COLOR_PALETTE.length]}
                      isAnimationActive={true}
                      animationDuration={400}
                    />
                  ) : null
                )}
              </BarChart>
            ) : (
              <LineChart
                data={data
                  .map((d) => ({
                    ...d,
                    __millis: DateTime.fromFormat(
                      d.timestamp,
                      "M/d/yyyy, h:mm:ss a"
                    ).toMillis(),
                  }))
                  .sort((a, b) => a.__millis - b.__millis)}
                margin={{ top: 20, right: 30, left: 30, bottom: 90 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={(val) =>
                    DateTime.fromFormat(val, "M/d/yyyy, h:mm:ss a").toFormat(
                      "HH:mm:ss"
                    )
                  }
                  interval="preserveStartEnd"
                  tick={{ fontSize: 13, angle: -30, textAnchor: "end" }}
                  height={80}
                  label={{
                    value: "Time",
                    position: "insideBottom",
                    offset: 0,
                    fontSize: 13,
                    fill: "#333",
                  }}
                />
                <YAxis
                  label={{
                    value: "Value",
                    angle: -90,
                    position: "insideLeft",
                    offset: -10,
                    fontSize: 14,
                    fill: "#333",
                  }}
                />
                <Tooltip />
                {keys.map((key, index) =>
                  visibleSeries[key] ? (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={COLOR_PALETTE[index % COLOR_PALETTE.length]}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={true}
                      animationDuration={400}
                    />
                  ) : null
                )}
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </Box>
    </ChartContainer>
  );
};

ChartBlock.propTypes = {
  title: PropTypes.node.isRequired,
  data: PropTypes.array.isRequired,
  keys: PropTypes.array.isRequired,
  visibleSeries: PropTypes.object.isRequired,
  toggleSeries: PropTypes.func.isRequired,
  extra: PropTypes.node,
  connectionStatus: PropTypes.string.isRequired,
  useBarChart: PropTypes.bool,
  isFilterActive: PropTypes.bool,
};

CustomLegend.propTypes = {
  seriesKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  visibleSeries: PropTypes.object.isRequired,
  toggleSeries: PropTypes.func.isRequired,
};

ValveStateCard.propTypes = {
  state: PropTypes.number.isRequired,
};

ValveCountCard.propTypes = {
  openCount: PropTypes.number.isRequired,
  closeCount: PropTypes.number.isRequired,
};

ChartContainer.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

ChartBlock.propTypes = {
  title: PropTypes.node.isRequired,
  data: PropTypes.array.isRequired,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired,
  visibleSeries: PropTypes.object.isRequired,
  toggleSeries: PropTypes.func.isRequired,
  extra: PropTypes.node,
  connectionStatus: PropTypes.string.isRequired,
  useBarChart: PropTypes.bool,
  isFilterActive: PropTypes.bool,
};

export {
  ChartBlock,
  CustomLegend,
  ChartContainer,
  ValveStateCard,
  ValveCountCard,
};
